import Currency from "react-currency-formatter";
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import theme from "styles/theme";

import { AiOutlineInfoCircle } from "react-icons/ai";
import { FaRegCopy } from "react-icons/fa6";
import { FiDownload } from "react-icons/fi";
import { ImSpinner6 } from "react-icons/im";
import { normalizeBillStatus } from "utils/functions";
import { toast } from "react-toastify";
import { useGaEvents } from "../hooks/useGaEvents";
import { useQuickBillContext } from "../hooks/useQuickBill";

import {
  ActionSpinner,
  Bullet,
  BulletMobile,
  Container,
  ContainerItems,
  ContentActions,
  ContentDate,
  ContentItem,
  InfoCondominium,
  InfoCondominiumWrapper,
  InfoContent,
  InfosWrapper,
  ItemContent,
  ItemTitle,
  Month,
  OptionItem,
  OptionLabel,
  OptionsContainer,
  OptionsWrapper,
  TooltipAiOutlineInfoCircle,
  Year,
} from "./styles";

interface IBoletoItem {
  installment: any;
  invoice: any;
}

const BoletoItem = ({ installment, invoice }: IBoletoItem): JSX.Element => {
  const [status, setStatus] = useState("");
  const [openOptions, setOpenOptions] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  const normalizedStatus = normalizeBillStatus(
    installment?.status,
    invoice?.totalAmount
  );

  const {
    handleClickCopyBarCode,
    handleClickDownloadResumeBill,
    handleClickDownloadCompleteBill,
  } = useGaEvents();

  const {
    selectedUnit,
    document: documentContext,
    requestToken,
    setSelectedInvoice,
    setSelectedInstallment,
    handleDownloadBill,
  } = useQuickBillContext();

  const copy = (): void => {
    handleClickCopyBarCode(documentContext, selectedUnit, installment);
    setStatus("copy");
    navigator.clipboard.writeText(installment.typeableLine);
    toast.success("Código de barras copiado");
    setStatus("");
  };

  const handleCompletDownload = async (): Promise<void> => {
    handleClickDownloadCompleteBill(documentContext, selectedUnit, installment);
    setStatus("download");

    const token = await requestToken();

    setSelectedInvoice(invoice);
    setSelectedInstallment(installment);

    if (token) {
      await handleDownloadBill(false, token, installment, invoice);
    } else {
      toast.error(
        "Ocorreu um erro ao tentar baixar o boleto, tente novamente mais tarde!"
      );
    }
    setStatus("");
  };

  const handleSimpleDownload = async (): Promise<void> => {
    handleClickDownloadResumeBill(documentContext, selectedUnit, installment);
    setStatus("download");
    setSelectedInvoice(invoice);
    setSelectedInstallment(installment);
    await handleDownloadBill(true, "", installment, invoice);
    setOpenOptions(false);
    setStatus("");
  };

  function handleClickOutside(event: MouseEvent): void {
    if (
      optionsRef.current &&
      !optionsRef.current.contains(event.target as Node)
    ) {
      setOpenOptions(false);
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionsRef]);

  return (
    <Container>
      <ContentDate>
        <BulletMobile status={normalizedStatus} />
        <Month>{moment(installment.dueDate).format("MMM")}</Month>
        <Year>{moment(installment.dueDate).format("YYYY")}</Year>
      </ContentDate>
      <InfosWrapper>
        <InfoCondominium>
          <InfoCondominiumWrapper>
            <ItemTitle>Condomínio:</ItemTitle>
            <InfoContent>{installment?.condominiumName}</InfoContent>
          </InfoCondominiumWrapper>
          <InfoCondominiumWrapper>
            <ItemTitle>Unidade:</ItemTitle>
            <InfoContent>
              {`${installment?.condominiumAddressNumber} ${
                installment?.unitComplement ? "-" : ""
              }
            ${installment?.unitComplement || ""}`}
            </InfoContent>
          </InfoCondominiumWrapper>
        </InfoCondominium>
        <ContainerItems>
          <ContentItem>
            <ItemTitle>Referência</ItemTitle>
            <ItemContent>
              {moment(installment.referenceMonth).format("MM/YYYY")}
            </ItemContent>
          </ContentItem>
          <ContentItem>
            <ItemTitle>Vencimento</ItemTitle>
            <ItemContent>
              {moment(installment.dueDate).format("DD/MM/YYYY")}
            </ItemContent>
          </ContentItem>
          <ContentItem>
            <ItemTitle>Valor</ItemTitle>
            <ItemContent>
              {installment.amountOriginal ? (
                <Currency
                  quantity={installment.amountOriginal}
                  currency="BRL"
                  locale="pt"
                />
              ) : (
                `R$ ${invoice.totalAmount}`
              )}
            </ItemContent>
          </ContentItem>
          <ContentItem showMobile={false}>
            <ItemTitle>Situação</ItemTitle>
            <ItemContent>
              <Bullet status={normalizedStatus} />
              {normalizedStatus?.replace("_", " ")}
            </ItemContent>
          </ContentItem>
          <ContentActions>
            {status === "copy" ? (
              <ActionSpinner>
                <ImSpinner6 />
              </ActionSpinner>
            ) : (
              <>
                <FaRegCopy
                  data-tip="info-copy"
                  data-for="info-copy"
                  onClick={copy}
                />
                <TooltipAiOutlineInfoCircle
                  size="small"
                  id="info-copy"
                  place="left"
                  type="dark"
                  effect="solid"
                  offset={{
                    left: 10,
                    right: 10,
                    top: 10,
                    bottom: 10,
                  }}
                  arrowColor={theme.colors.blueClean}
                  backgroundColor={theme.colors.blueClean}
                  textColor={theme.colors.blue}
                >
                  <p>Copiar código de barras</p>
                </TooltipAiOutlineInfoCircle>
              </>
            )}
            {status === "download" ? (
              <ActionSpinner>
                <ImSpinner6 />
              </ActionSpinner>
            ) : (
              <OptionsContainer ref={optionsRef}>
                <FiDownload onClick={() => setOpenOptions((prev) => !prev)} />
                {openOptions && (
                  <OptionsWrapper>
                    <OptionItem onClick={handleSimpleDownload}>
                      <OptionLabel>Download resumido</OptionLabel>
                    </OptionItem>
                    <OptionItem onClick={handleCompletDownload}>
                      <OptionLabel>Download completo</OptionLabel>
                    </OptionItem>
                  </OptionsWrapper>
                )}
              </OptionsContainer>
            )}
            <AiOutlineInfoCircle
              data-tip="info-compensacao"
              data-for="info-compensacao"
            />
            <TooltipAiOutlineInfoCircle
              id="info-compensacao"
              place="left"
              type="dark"
              effect="solid"
              offset={{
                left: 10,
                right: 10,
                top: 10,
                bottom: 10,
              }}
              arrowColor={theme.colors.blueClean}
              backgroundColor={theme.colors.blueClean}
              textColor={theme.colors.blue}
            >
              <span>Tempo de compensação do boleto</span>
              <p>
                A Ultragaz recebe a confirmação de pagamento, em média, 3 dias
                úteis após o pagamento. Após este prazo o status de seu boleto
                será atualizado em nossos canais.
              </p>
            </TooltipAiOutlineInfoCircle>
          </ContentActions>
        </ContainerItems>
      </InfosWrapper>
    </Container>
  );
};

export default BoletoItem;
